import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import RangeDatePicker from 'modules/shared/common/components/RangeDatePicker';
import Modal from 'modules/shared/common/components/Modal';
import Filter from 'modules/shared/common/components/Filter';
import SearchByItemsFilter from 'modules/shared/orders/components/SearchByItemsFilter';
import {
  OrdersFilterWrapper,
  OrdersFilterSearchBarWrapper,
  OrdersFilterSearchBar,
  OrdersFilterSearchBarIcon,
  MoreFiltersButton,
  MoreFitlersClearIcon,
  OrdersFilterClearIcon,
} from './OrdersFilter.style';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrdersFilter = ({
  stringsData,
  selectedDateFilterObj,
  handleOrderSearchBarInputChange,
  handleOrderDateFilterChange,
  handleGenericFilterClick,
  handleGenericMultiFitlerClick,
  ordersApiCallParams,
  filters,
}) => {
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMoreFiltersData, setSelectedMoreFiltersData] = useState({});
  const [isSelectedMoreFiltersData, setIsSelectedMoreFiltersData] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    let isSelected = 0;
    // eslint-disable-next-line no-restricted-syntax
    Object.entries(selectedMoreFiltersData).forEach(([key, value]) => {
      if (Object.keys(value).length > 0 && ordersApiCallParams.hasOwnProperty(key)) {
        isSelected += 1;
      }
    });
    setIsSelectedMoreFiltersData(isSelected);
  }, [selectedMoreFiltersData, ordersApiCallParams]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################

  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        handleOrderSearchBarInputChange(value);
      }, 300);
    }
    // this condition checks if we are deleteing letters from search query and we reached
    // 2 characters, then we need to clear search query filter
    else if (searchQuery.length >= searchQueryMinLength && value.length < searchQueryMinLength)
      handleOrderSearchBarInputChange(value, true);
  };

  const clearMoreFilters = () => {
    const tempMoreFiltersData = { ...selectedMoreFiltersData };
    Object.keys(selectedMoreFiltersData).forEach(key => {
      tempMoreFiltersData[key] = {};
    });
    handleGenericMultiFitlerClick(tempMoreFiltersData, () => {});
    setSelectedMoreFiltersData({});
  };

  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <OrdersFilterWrapper>
      <OrdersFilterSearchBarWrapper>
        <OrdersFilterSearchBarIcon />
        <OrdersFilterSearchBar
          placeholder={stringsData.ordersHeader.ordersFilterSearchBarPlaceholder}
          value={searchQuery}
          onChange={event => handleSearchBarInputChange(event)}
        />
        {searchQuery && (
          <OrdersFilterClearIcon
            onClick={() => {
              setSearchQuery('');
              handleOrderSearchBarInputChange('', true);
            }}
          />
        )}
      </OrdersFilterSearchBarWrapper>
      <RangeDatePicker
        selectedDateFilterObj={selectedDateFilterObj}
        handleRangeDatePickerChange={handleOrderDateFilterChange}
      />
      {filters?.dropDownFilters?.map(filterProps => {
        let isDisplayFilter = true;
        if (filterProps?.filterDisplayCondition)
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of Object.entries(filterProps.filterDisplayCondition)) {
            if (ordersApiCallParams.hasOwnProperty(key)) {
              if (Array.isArray(value)) {
                for (let i = 0; i < value.length; i += 1) {
                  const val = value[i].toString();
                  if (ordersApiCallParams[key].includes(val)) {
                    isDisplayFilter = true;
                    break;
                  } else isDisplayFilter = false;
                }
              } else isDisplayFilter = true;
            } else isDisplayFilter = false;
          }

        return (
          isDisplayFilter && (
            <Filter
              key={filterProps.filterKey}
              handleGenericFilterClick={handleGenericFilterClick}
              filterProps={filterProps}
              ordersApiCallParams={ordersApiCallParams}
            />
          )
        );
      })}
      {filters?.moreFilters && (
        <>
          <MoreFiltersButton
            selected={isSelectedMoreFiltersData > 0}
            onClick={() => setIsModalOpen(prevState => !prevState)}
          >
            {stringsData.ordersHeader.MoreFiltersButton}
            {isSelectedMoreFiltersData > 0 && (
              <>
                {` (${isSelectedMoreFiltersData})`}
                <MoreFitlersClearIcon
                  onClick={e => {
                    e.stopPropagation();
                    clearMoreFilters();
                  }}
                />
              </>
            )}
          </MoreFiltersButton>
          <Modal
            title={stringsData.ordersHeader.MoreFiltersButton}
            onClose={() => setIsModalOpen(false)}
            toggle={isModalOpen}
            onConfirmHandler={() => {
              handleGenericMultiFitlerClick(selectedMoreFiltersData, setIsModalOpen);
            }}
            confirmActionLabel={stringsData.ordersHeader.MoreFiltersConfirmButtonLabel}
            onCancelHandler={() => {
              clearMoreFilters();
            }}
            cancelActionLabel={stringsData.ordersHeader.MoreFiltersCancelButtonLabel}
          >
            {filters?.searchByItemsFilter && (
              <SearchByItemsFilter
                selectedItems={selectedMoreFiltersData}
                setSelectedItems={setSelectedMoreFiltersData}
              />
            )}
            {filters?.moreFilters?.map(filterProps => (
              <Filter
                extentedFilter
                key={filterProps.filterKey}
                handleGenericFilterClick={handleGenericFilterClick}
                filterProps={filterProps}
                selectedMoreFiltersData={selectedMoreFiltersData}
                setSelectedMoreFiltersData={setSelectedMoreFiltersData}
              />
            ))}
          </Modal>
        </>
      )}
    </OrdersFilterWrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrdersFilter.propTypes = {
  stringsData: PropTypes.shape({
    ordersHeader: PropTypes.shape({
      ordersFilterSearchBarPlaceholder: PropTypes.string,
      MoreFiltersButton: PropTypes.string,
      MoreFiltersConfirmButtonLabel: PropTypes.string,
      MoreFiltersCancelButtonLabel: PropTypes.string,
    }),
  }).isRequired,
  selectedDateFilterObj: PropTypes.shape({}).isRequired,
  handleOrderSearchBarInputChange: PropTypes.func.isRequired,
  handleOrderDateFilterChange: PropTypes.func.isRequired,
  handleGenericFilterClick: PropTypes.func.isRequired,
  handleGenericMultiFitlerClick: PropTypes.func.isRequired,
  ordersApiCallParams: PropTypes.shape({}).isRequired,
  filters: PropTypes.shape({
    dropDownFilters: PropTypes.arrayOf(PropTypes.shape({})),
    moreFilters: PropTypes.arrayOf(PropTypes.shape({})),
    searchByItemsFilter: PropTypes.bool,
  }),
};

OrdersFilter.defaultProps = {
  filters: {},
};

export default OrdersFilter;
